// @flow
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { callContract, contractFetcher } from "../../../lib/contracts";
import Token from "../../../abis/Token.json";
import { formatAmount, formatAmountFree, parseValue } from "../../../lib/numbers";
import { approveTokens } from "../../../domain/tokens";
import { BigNumberish, ethers } from "ethers";
import RewardRouter from "../../../abis/RewardRouter.json";
import classes from "./StakeModalStyles.module.scss";
import { t, Trans } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "../../../lib/chains";
import { ModalBase } from "../ModalBase/ModalBase";
import { ActionButton } from "../../Buttons/ActionButton/ActionButton";
import classNames from "classnames";

type Props = {
  onClose: () => void;
  title: string;
  maxAmount: string;
  stakingTokenSymbol: string;
  stakingTokenAddress: any;
  farmAddress: string;
  stakeMethodName: string;
  rewardRouterAddress: string;
  setPendingTxns: any;
};

export const StakeModal = ({
  onClose,
  title,
  maxAmount,
  stakingTokenSymbol,
  stakingTokenAddress,
  farmAddress,
  rewardRouterAddress,
  stakeMethodName,
  setPendingTxns,
}: Props) => {
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();
  const { AddressZero } = ethers.constants;
  const [isStaking, setIsStaking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [value, setValue] = useState("");
  const [needApproval, setNeedApproval] = useState(false);
  const [amount, setAmount] = useState<ethers.BigNumber | undefined>();

  const { data: tokenAllowance } = useSWR(
    active && stakingTokenAddress && [active, chainId, stakingTokenAddress, "allowance", account, farmAddress],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  useEffect(() => {
    setAmount(parseValue(value, 18));
  }, [value]);

  useEffect(() => {
    setNeedApproval(
      farmAddress !== AddressZero && !!tokenAllowance && !!amount && amount.gt(tokenAllowance as ethers.BigNumber)
    );
  }, [amount, tokenAllowance, farmAddress]);

  const getError = useCallback(() => {
    if (!amount || amount.eq(0)) {
      return t`Enter an amount`;
    }
    if (maxAmount && amount.gt(maxAmount)) {
      return t`Max amount exceeded`;
    }
  }, [amount]);

  const onClickPrimary = () => {
    if (needApproval) {
      //TODO: this function is missing some props, need to take a look

      // @ts-ignore
      approveTokens({
        setIsApproving,
        library,
        tokenAddress: stakingTokenAddress,
        spender: farmAddress,
        chainId,
      });
      return;
    }

    setIsStaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, library.getSigner());

    callContract(chainId, contract, stakeMethodName, [amount], {
      sentMsg: t`Stake submitted!`,
      failMsg: t`Stake failed.`,
      setPendingTxns,
    })
      .then(async (res) => {
        console.log(res);
        onClose();
      })
      .finally(() => {
        setIsStaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isApproving) {
      return false;
    }
    return !isStaking;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isApproving) {
      return t`Approving ${stakingTokenSymbol}...`;
    }
    if (needApproval) {
      return t`Approve ${stakingTokenSymbol}`;
    }
    if (isStaking) {
      return t`Staking...`;
    }
    return t`Stake`;
  };

  return (
    <ModalBase title={title} onClose={onClose}>
      <div className={classes.modalWrapper}>
        <div className={classes.inputWrapper}>
          <div className={classes.content}>
            <span className={classes.title}>
              <Trans>Stake</Trans>
            </span>
            <span
              className={classNames(classes.title, classes.titleClickable)}
              onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}
            >
              <Trans>Max</Trans>: {formatAmount(maxAmount, 18, 4, true)}
            </span>
          </div>
          <div className={classes.content}>
            <input
              type="number"
              placeholder="0.0"
              className={classes.input}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <span className={classes.symbolTitle}>{stakingTokenSymbol}</span>
          </div>
        </div>

        <ActionButton
          onClick={onClickPrimary}
          type={"outlined"}
          buttonText={getPrimaryText()}
          disabled={!isPrimaryEnabled()}
          className={classes.button}
        />
      </div>
    </ModalBase>
  );
};
